import React, { useState, useEffect, useMemo } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeToPoolBookings, subscribeToTargetedOffersForDriver, subscribeToActiveTrips } from '../../services/tripService';
import { claimBooking, declineTargetedOffer } from '../../services/bookingService';
import toast from 'react-hot-toast';

const TIER_ORDER = ['go', 'comfy', 'plus', 'vip', 'vipPlus'];
const TIER_LABELS = { go: 'Go', comfy: 'Comfy', plus: 'Plus', vip: 'VIP', vipPlus: 'VIP+' };
const TIER_COLORS = {
  go: '#2dd4a8', comfy: '#3b82f6', plus: '#8b5cf6', vip: '#fbbf24', vipPlus: '#ff6b6b',
};

// Visibility delay tier defaults (must match config/bookingRules.quality.tierBaseDelaySeconds)
const TIER_BASE_DELAY = { gold: 0, silver: 60, bronze: 180, probation: 600 };

export default function AvailableBookings() {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const [poolBookings, setPoolBookings] = useState([]);
  const [targetedOffers, setTargetedOffers] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [isOnline, setIsOnline] = useState(userProfile?.isOnline ?? true);
  const [eligibility, setEligibility] = useState(null); // { eligible, reasons }
  const [togglingOnline, setTogglingOnline] = useState(false);
  const [, forceTick] = useState(0);

  // Tick every 5s so visibility-delay filters re-evaluate without manual refresh
  useEffect(() => {
    const id = setInterval(() => forceTick(t => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  // Load the driver's eligibility so we can show what's blocking them from going online.
  useEffect(() => {
    const check = httpsCallable(functions, 'getMyEligibility');
    check({}).then((r) => setEligibility(r.data)).catch(() => {});
  }, []);

  // Driver eligibility tiers (own tier + below)
  const eligibleTiers = useMemo(() => {
    if (!userProfile?.serviceTier) return [];
    const idx = TIER_ORDER.indexOf(userProfile.serviceTier);
    if (idx < 0) return [];
    return TIER_ORDER.slice(0, idx + 1);
  }, [userProfile?.serviceTier]);

  const driverQualityTier = userProfile?.driverStats?.adminOverrideTier
    || userProfile?.driverStats?.qualityTier
    || 'gold';

  // Subscribe to pool, targeted, and confirmed trips (for time-conflict check)
  useEffect(() => {
    if (!isOnline || eligibleTiers.length === 0 || !currentUser?.uid) {
      setPoolBookings([]); setTargetedOffers([]); setActiveTrips([]);
      return;
    }
    const unsub1 = subscribeToPoolBookings(eligibleTiers, setPoolBookings);
    const unsub2 = subscribeToTargetedOffersForDriver(currentUser.uid, setTargetedOffers);
    const unsub3 = subscribeToActiveTrips(currentUser.uid, 'driver', setActiveTrips);
    return () => { unsub1 && unsub1(); unsub2 && unsub2(); unsub3 && unsub3(); };
  }, [isOnline, eligibleTiers, currentUser?.uid]);

  // Online toggle — persists to Firestore
  const toggleOnline = async () => {
    const next = !isOnline;
    setTogglingOnline(true);
    try {
      // Server enforces eligibility on the way ONLINE; offline is always allowed.
      const fn = httpsCallable(functions, 'setDriverOnline');
      await fn({ online: next });
      setIsOnline(next);
      if (next) setEligibility({ eligible: true, reasons: [] });
    } catch (err) {
      const reasons = err && err.details && err.details.reasons;
      if (Array.isArray(reasons) && reasons.length) {
        setEligibility({ eligible: false, reasons });
        toast.error("You can't go online yet — see the checklist below.");
      } else {
        toast.error((err && err.message) || 'Failed to update status');
      }
    }
    setTogglingOnline(false);
  };

  // Filter pool bookings client-side: cool-off, capacity, WWCC, child seat, time conflict, visibility delay
  const visiblePoolBookings = useMemo(() => {
    if (!isOnline || !userProfile) return [];
    const now = Date.now();
    const cooloffMs = 60 * 60 * 1000; // matches config default
    const baseDelaySec = TIER_BASE_DELAY[driverQualityTier] ?? 0;
    const myPickupTimes = activeTrips.map(t =>
      t.scheduledPickupTime?.toDate ? t.scheduledPickupTime.toDate().getTime() : null
    ).filter(Boolean);

    return poolBookings.filter(bk => {
      // Cool-off
      const declinedAt = bk.previouslyDeclinedAt?.[currentUser.uid];
      if (declinedAt) {
        const dt = declinedAt.toDate ? declinedAt.toDate().getTime() : new Date(declinedAt).getTime();
        if (now - dt < cooloffMs) return false;
      }
      // Time conflict (±90min with any confirmed trip)
      const pickup = bk.scheduledPickupTime?.toDate ? bk.scheduledPickupTime.toDate().getTime() : null;
      if (!pickup) return false;
      if (myPickupTimes.some(t => Math.abs(t - pickup) < 90 * 60000)) return false;
      // Visibility delay (scaled by time-until-pickup)
      const pendingSince = bk.pendingPoolSince?.toDate ? bk.pendingPoolSince.toDate().getTime() : null;
      if (pendingSince && baseDelaySec > 0) {
        const hoursToPickup = (pickup - now) / 3600000;
        let scaledDelay = baseDelaySec;
        if (hoursToPickup <= 2) scaledDelay = 0;
        else if (hoursToPickup < 24) scaledDelay = baseDelaySec * ((hoursToPickup - 2) / 22);
        const ageSec = (now - pendingSince) / 1000;
        if (ageSec < scaledDelay) return false;
      }
      return true;
    });
}, [isOnline, poolBookings, activeTrips, userProfile, currentUser?.uid, driverQualityTier]);

  // Sorted: own-tier first, then by pickup time
  const sortedPool = useMemo(() => {
    const ownTier = userProfile?.serviceTier;
    return [...visiblePoolBookings].sort((a, b) => {
      const aOwn = a.tier === ownTier ? 0 : 1;
      const bOwn = b.tier === ownTier ? 0 : 1;
      if (aOwn !== bOwn) return aOwn - bOwn;
      const aT = a.scheduledPickupTime?.toDate ? a.scheduledPickupTime.toDate().getTime() : 0;
      const bT = b.scheduledPickupTime?.toDate ? b.scheduledPickupTime.toDate().getTime() : 0;
      return aT - bT;
    });
  }, [visiblePoolBookings, userProfile?.serviceTier]);

  const handleClaim = async (bookingId, ref) => {
    if (busyId) return;
    setBusyId(bookingId);
    try {
      await claimBooking(bookingId);
      toast.success(`You've claimed ${ref}!`);
      navigate(`/driver/trip/${bookingId}`);
    } catch (err) {
      const msg = err?.message || 'Failed to claim booking';
      if (msg.includes('no longer available') || msg.includes('failed-precondition')) {
        toast.error('Sorry, another driver just took this one.');
      } else {
        toast.error(msg);
      }
      setBusyId(null);
    }
  };

  const handleDecline = async (bookingId, ref) => {
    if (busyId) return;
    if (!window.confirm('Decline this offer? It will go to the pool.')) return;
    setBusyId(bookingId);
    try {
      await declineTargetedOffer(bookingId);
      toast.success(`Declined ${ref}`);
    } catch (err) {
      toast.error(err?.message || 'Failed to decline');
    }
    setBusyId(null);
  };

  if (!userProfile) return <div className="p-10 text-center">Loading...</div>;

  return (
    <div className="max-w-[700px] mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-5">
        <button onClick={() => navigate('/driver')} className="bg-none border-0 cursor-pointer text-brand-primary font-semibold text-[0.9rem]">← Dashboard</button>
        <h1 className="font-serif m-0 text-brand-dark text-[1.4rem]">
          Available Bookings
        </h1>
        <div />
      </div>

      {/* Online toggle */}
      <div className="flex items-center justify-between p-4 rounded-2xl mb-5"
        style={{
          background: isOnline ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${isOnline ? '#bbf7d0' : '#fecaca'}`,
        }}>
        <div>
          <div className="font-bold" style={{ color: isOnline ? '#059669' : '#dc2626' }}>
            {isOnline ? '🟢 Online — accepting bookings' : '⚫ Offline'}
          </div>
          <div className="text-[0.8rem] text-body mt-0.5">
            {isOnline ? `Tier: ${driverQualityTier}${userProfile?.driverStats?.inGracePeriod ? ' (grace period)' : ''}` : 'No bookings will be shown'}
          </div>
        </div>
        <button onClick={toggleOnline} disabled={togglingOnline} className="px-5 py-2.5 rounded-full cursor-pointer font-bold text-[0.9rem]"
          style={{
            background: isOnline ? '#fff' : '#2dd4a8',
            color: isOnline ? '#0a1628' : '#fff',
            border: isOnline ? '1.5px solid #e2e8f0' : 'none',
            opacity: togglingOnline ? 0.6 : 1,
          }}>{togglingOnline ? '…' : (isOnline ? 'Go Offline' : 'Go Online')}</button>
      </div>

      {/* Eligibility checklist — shown when the driver can't go online */}
      {eligibility && !eligibility.eligible && (
        <div className="p-4 rounded-2xl mb-5" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
          <div className="font-bold mb-1" style={{ color: '#92400e' }}>You can't go online yet</div>
          <div className="text-[0.85rem] text-body mb-2">Resolve the following to accept bookings:</div>
          <ul className="list-disc pl-5 text-[0.85rem] text-body" style={{ margin: 0 }}>
            {eligibility.reasons.map((rsn, i) => <li key={i} className="mb-0.5">{rsn}</li>)}
          </ul>
        </div>
      )}

      {/* Targeted Offers Section */}
      {isOnline && targetedOffers.length > 0 && (
        <div className="mb-6">
          <h2 className="font-serif text-[1.1rem] text-brand-dark mt-0 mb-2.5">
            ✉️ Offers for you ({targetedOffers.length})
          </h2>
          <div className="text-[0.85rem] text-body mb-3">
            These bookings are offered exclusively to you. Accept or decline.
          </div>
          {targetedOffers.map(bk => (
            <BookingCard
              key={bk.id}
              booking={bk}
              isTargeted={true}
              expanded={expandedId === bk.id}
              onToggle={() => setExpandedId(expandedId === bk.id ? null : bk.id)}
              onClaim={() => handleClaim(bk.id, bk.referenceNumber)}
              onDecline={() => handleDecline(bk.id, bk.referenceNumber)}
              busy={busyId === bk.id}
            />
          ))}
        </div>
      )}

      {/* Pool Section */}
      {isOnline && (
        <div>
          <h2 className="font-serif text-[1.1rem] text-brand-dark mt-0 mb-2.5">
            📋 Available bookings ({sortedPool.length})
          </h2>
          {sortedPool.length === 0 ? (
            <div className="p-8 rounded-2xl bg-slate-50 text-center text-muted">
              No bookings available right now.
              <div className="text-[0.8rem] mt-1.5">New bookings will appear here automatically.</div>
            </div>
          ) : (
            sortedPool.map(bk => (
              <BookingCard
                key={bk.id}
                booking={bk}
                isTargeted={false}
                expanded={expandedId === bk.id}
                onToggle={() => setExpandedId(expandedId === bk.id ? null : bk.id)}
                onClaim={() => handleClaim(bk.id, bk.referenceNumber)}
                onDecline={null}
                busy={busyId === bk.id}
              />
            ))
          )}
        </div>
      )}
      <BackButtonBottom to="/driver" label="Dashboard" />
    </div>
  );
}

// ═══════════════ BOOKING CARD ═══════════════

function BookingCard({ booking, isTargeted, expanded, onToggle, onClaim, onDecline, busy }) {
  const tierColor = TIER_COLORS[booking.tier] || '#5a6578';
  const tierLabel = TIER_LABELS[booking.tier] || booking.tier;
  const pickupTime = booking.scheduledPickupTime?.toDate ? booking.scheduledPickupTime.toDate() : null;
  const fareTotal = booking.fareBreakdown?.totalFare?.toFixed(2) || '—';
  const distance = booking.estimatedDistance || 0;
  const duration = booking.estimatedDuration || 0;

  // Relative time ("in 3 hours", "tomorrow")
  const relativeTime = (() => {
    if (!pickupTime) return '';
    const minutes = (pickupTime - Date.now()) / 60000;
    if (minutes < 60) return `in ${Math.max(0, Math.round(minutes))} min`;
    if (minutes < 1440) return `in ${Math.round(minutes / 60)} hr`;
    return `in ${Math.round(minutes / 1440)} day${Math.round(minutes / 1440) > 1 ? 's' : ''}`;
  })();

  // Targeted offer countdown
  const offerSecondsRemaining = (() => {
    if (!isTargeted || !booking.offerExpiresAt) return null;
    const exp = booking.offerExpiresAt.toDate ? booking.offerExpiresAt.toDate() : new Date(booking.offerExpiresAt);
    return Math.max(0, Math.floor((exp - Date.now()) / 1000));
  })();
  const formatCountdown = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const cardBg = isTargeted ? '#faf5ff' : '#fff';
  const borderColor = isTargeted ? '#a855f7' : '#e2e8f0';

  return (
    <div className="p-4 rounded-2xl mb-3"
      style={{ background: cardBg, border: `1.5px solid ${borderColor}` }}>
      {/* Top row: tier badge + relative time + fare */}
      <div className="flex justify-between items-center mb-2.5">
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-full text-white text-[0.7rem] font-bold"
            style={{ background: tierColor, letterSpacing: 0.5 }}>{tierLabel.toUpperCase()}</span>
          <span className="text-[0.85rem] text-body">
            {pickupTime ? pickupTime.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : ''}
          </span>
          <span className="text-[0.75rem] text-muted">· {relativeTime}</span>
        </div>
        <div className="font-extrabold text-[1.1rem] text-brand-dark">${fareTotal}</div>
      </div>

      {/* Targeted countdown banner */}
      {isTargeted && offerSecondsRemaining != null && (
        <div className="p-2 rounded-lg bg-white text-[0.85rem] text-center mb-2.5 font-semibold"
          style={{ border: '1px solid #e9d5ff', color: '#6b21a8' }}>
          {offerSecondsRemaining > 0
            ? `⏱ Respond within ${formatCountdown(offerSecondsRemaining)}`
            : 'Offer expired'}
        </div>
      )}

      {/* Trip basics */}
      <div className="mb-2.5">
        <div className="flex items-start gap-2 mb-1">
          <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: '#2dd4a8' }} />
          <span className="text-[0.9rem] text-brand-dark">{truncateAddr(booking.pickupAddress)}</span>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: '#ff6b6b' }} />
          <span className="text-[0.9rem] text-brand-dark">{truncateAddr(booking.destinationAddress)}</span>
        </div>
      </div>

      {/* Trip stats */}
      <div className="text-[0.8rem] text-muted mb-2.5">
        {distance}km · ~{duration} min · {booking.passengerCount || 1} pax
        {booking.fareBreakdown?.tolls > 0 && ` · toll $${booking.fareBreakdown.tolls.toFixed(2)}`}
        {booking.hasMinor && ' · 👶 minor'}
        {booking.childSeatRequired && ' · 🪑 child seat'}
        {booking.accessibilityNeeded && ' · ♿ accessibility'}
      </div>

      {/* Expandable details */}
      {expanded && (
        <div className="p-3 rounded-[10px] bg-slate-50 mb-2.5 text-[0.85rem]">
          <div className="mb-1.5"><strong>Full pickup:</strong> {booking.pickupAddress}</div>
          <div className="mb-1.5"><strong>Full destination:</strong> {booking.destinationAddress}</div>
          {booking.routeSummary && <div className="mb-1.5"><strong>Route:</strong> via {booking.routeSummary}</div>}
          <div className="mb-1.5"><strong>Rider:</strong> {booking.riderName || 'Anonymous'}</div>
          {booking.luggage && (booking.luggage.small > 0 || booking.luggage.large > 0) && (
            <div className="mb-1.5">
              <strong>Luggage:</strong> {booking.luggage.small || 0} small + {booking.luggage.large || 0} large
            </div>
          )}
          {booking.specialInstructions && (
            <div className="mb-1.5"><strong>Notes:</strong> {booking.specialInstructions}</div>
          )}
          <div className="mb-1.5 text-[0.75rem] text-muted">
            Ref: {booking.referenceNumber}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={onToggle} className="flex-none px-4 py-2.5 rounded-full border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.85rem]">
          {expanded ? 'Less' : 'Details'}
        </button>
        {isTargeted && onDecline && (
          <button onClick={onDecline} disabled={busy} className="flex-none px-5 py-3 rounded-xl bg-white cursor-pointer font-semibold text-[0.9rem]"
            style={{ border: '1.5px solid #fecaca', color: '#dc2626' }}>
            Decline
          </button>
        )}
        <button onClick={onClaim} disabled={busy} className="flex-1 px-5 py-3 rounded-xl border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.9rem]">
          {busy ? 'Claiming...' : isTargeted ? 'Accept' : 'Claim'}
        </button>
      </div>
    </div>
  );
}

function truncateAddr(addr) {
  if (!addr) return '—';
  return addr.length > 50 ? addr.slice(0, 47) + '…' : addr;
}